if (!customElements.get('l-main-product')) {
	class LMainProduct extends HTMLElement {
		constructor() {
			super()
			this.sectionTimelines = new WeakMap()
			this.sectionTriggers = []
		}

		connectedCallback() {
			document.addEventListener('l:vendor:loaded', () => {
				this._init()
			})
		}

		/* =====================================
			Init
		===================================== */
		_init() {
			this.mm = gsap.matchMedia()

			this.mm.add('(min-width: 1024px)', () => {
				document.documentElement.style.overflow = 'hidden'
				window.scrollTo(0, 0)

				this._getElements()
				this._clearAOSAttributes()

				requestAnimationFrame(() => {
					this._initHorizontalScroll()
					this._initSectionAnimations()

					ScrollTrigger.refresh()

					document.documentElement.style.overflow = ''

					gsap.to(this, { opacity: 1, duration: 0.5 })
				})

				return this._cleanUp.bind(this)
			})
		}

		/* =====================================
			Horizontal Scroll
		===================================== */
		_initHorizontalScroll() {
			this.scrollTween = gsap.to(this.cardEls, {
				x: () => -(this._computeMaxWidth() - window.innerWidth),
				ease: 'none',
				scrollTrigger: {
					trigger: this,
					start: () => {
						return `top 0px`
					},
					end: () => `+=${this._computeMaxWidth()}`,
					pin: true,
					scrub: 1,
					invalidateOnRefresh: true,
					refreshPriority: 1,
				},
			})
		}

		/* =====================================
			Section ScrollTriggers
		===================================== */
		_initSectionAnimations() {
			this.cardEls.forEach((cardEl) => {
				const tl = this._getSectionTimeline(cardEl)
				if (!tl) return

				const st = ScrollTrigger.create({
					trigger: cardEl,
					containerAnimation: this.scrollTween,
					start: 'right right',
					end: 'right left',
					scrub: true,
					animation: tl,
					invalidateOnRefresh: true,

					onUpdate: (self) => {
						if (self.progress >= 0.3 && self.progress < 0.9) {
							this._setNextActiveCard(cardEl)
						}
					},

					onToggle: (self) => {
						if (self.isActive) {
							this._setActiveCard(cardEl)
						}
					},
				})

				this.sectionTriggers.push(st)
			})
		}

		_cleanUp() {
			this.scrollTween?.scrollTrigger?.kill()
			this.scrollTween?.kill()

			this.sectionTriggers.forEach((st) => st.kill())
			this.sectionTriggers = []

			this.sectionTimelines.forEach?.((tl) => {
				tl._splits?.forEach((split) => split.revert())
			})
		}

		/* =====================================
			Helpers
		===================================== */
		_getElements() {
			this.cardEls = gsap.utils.toArray('.js-pdp-section', this)
		}

		_computeMaxWidth() {
			return this.cardEls.reduce((acc, el) => acc + el.offsetWidth, 0)
		}

		_getSectionTimeline(cardEl) {
			if (this.sectionTimelines.has(cardEl)) {
				return this.sectionTimelines.get(cardEl)
			}

			const type = cardEl.querySelector('[data-section]')?.dataset.section
			const tl = gsap.timeline({ paused: true })

			const fnName = `_section_${type}_animation`
			this[fnName]?.(cardEl, tl)

			this.sectionTimelines.set(cardEl, tl)
			return tl
		}

		_setActiveCard(activeCard) {
			this.cardEls.forEach((el) => {
				el.classList.toggle('is-active', el === activeCard)
			})
		}

		_setNextActiveCard(activeCard) {
			this.cardEls.forEach((el, index0) => {
				const nextEl = this.cardEls[index0 + 1]

				if (nextEl) {
					nextEl.classList.toggle('is-next-active', el === activeCard)
				}
			})
		}

		_clearAOSAttributes() {
			this.querySelectorAll('[data-aos]').forEach((el) => {
				el.removeAttribute('data-aos')
				el.removeAttribute('data-aos-delay')
				el.removeAttribute('data-aos-duration')
				el.removeAttribute('data-aos-once')
				el.removeAttribute('data-aos-offset')
				el.classList.remove('aos-init', 'aos-animate')
			})
		}

		/* =====================================
			ANIMATION HELPERS
		===================================== */
		_opacityYPercentAnimate(el, tl, opts = {}) {
			if (!el || !tl) return

			const { inAt = 0, outAt = 0.6, yTo = 100 } = opts

			gsap.set(el, { opacity: 1, yPercent: 0 })
			tl.to(el, { opacity: 0, yPercent: yTo, ease: 'power3.out' }, outAt)
		}

		/* =====================================
			SECTION: INTRO
		===================================== */
		_section_intro_animation(cardEl, tl) {
			const contentEls = cardEl.querySelectorAll('.js-content')
			const productImageEl = cardEl.querySelector('.js-product-image')

			contentEls.forEach((el) => this._opacityYPercentAnimate(el, tl))

			if (productImageEl) {
				gsap.set(productImageEl, { opacity: 1, scale: 1, xPercent: 0 })
				tl.to(productImageEl, { opacity: 0, scale: 0.6, xPercent: 100, ease: 'power3.out', duration: 0.6 }, 0)
			}
		}

		/* =====================================
			SECTION: PROCESS
		===================================== */
		_section_process_animation(cardEl, tl) {
			const contentEls = cardEl.querySelectorAll('.js-content')
			const mainImageEl = cardEl.querySelector('.js-main-image')
			const philosophyEl = cardEl.querySelector('.js-philosophy')

			if (mainImageEl) {
				gsap.set(mainImageEl, { opacity: 1, scale: 1 })
				tl.to(mainImageEl, { opacity: 0, scale: 0.2, ease: 'power3.out' }, 0)
			}

			contentEls.forEach((el) => this._opacityYPercentAnimate(el, tl))

			if (philosophyEl) {
				gsap.set(philosophyEl, { opacity: 1, scale: 1 })
				tl.to(philosophyEl, { opacity: 0, scale: 0.2, ease: 'power3.out' }, 0)
			}
		}

		/* =====================================
			SECTION: AGING
		===================================== */
		_section_aging_animation(cardEl, tl) {
			const leftContentEl = cardEl.querySelector('.js-left-content')
			const rightContentEl = cardEl.querySelector('.js-right-content')
			const headingEl = cardEl.querySelector('.js-heading')
			const topImageEl = cardEl.querySelector('.js-top-image')
			const bottomImageEl = cardEl.querySelector('.js-bottom-image')

			this._opacityYPercentAnimate(leftContentEl, tl)
			this._opacityYPercentAnimate(rightContentEl, tl)

			if (headingEl) {
				gsap.set(headingEl, { opacity: 1, xPercent: 0 })
				tl.to(headingEl, { opacity: 0, xPercent: 100 }, 0)
			}

			if (topImageEl) {
				gsap.set(topImageEl, { opacity: 1, yPercent: 0 })
				tl.to(topImageEl, { opacity: 0, yPercent: -100 }, 0)
			}

			if (bottomImageEl) {
				gsap.set(bottomImageEl, { opacity: 1, yPercent: 0 })
				tl.to(bottomImageEl, { opacity: 0, yPercent: 100 }, 0)
			}
		}

		/* =====================================
			SECTION: INGREDIENTS
		===================================== */
		_section_ingredients_animation(cardEl, tl) {
			const contentEls = cardEl.querySelectorAll('.js-content')
			const ingredientEls = cardEl.querySelectorAll('.js-ingredient')

			contentEls.forEach((el) => this._opacityYPercentAnimate(el, tl))

			ingredientEls.forEach((el) => {
				gsap.set(el, { opacity: 1, yPercent: 0 })
				tl.to(el, { opacity: 0, yPercent: -100 }, 0)
			})
		}

		/* =====================================
			SECTION: PAIRING
		===================================== */
		_section_pairing_animation(cardEl, tl) {
			const headingEl = cardEl.querySelector('.js-heading')
			const productImageEl = cardEl.querySelector('.js-product-image')

			if (productImageEl) {
				gsap.set(productImageEl, { opacity: 1, yPercent: 0 })
				tl.to(productImageEl, { opacity: 0, yPercent: 100, ease: 'power3.out' }, 0)
			}

			if (headingEl) {
				gsap.set(headingEl, { opacity: 1, xPercent: -50 })
				tl.to(headingEl, { opacity: 0, xPercent: 100 }, 0)
			}
		}
	}

	customElements.define('l-main-product', LMainProduct)
}

